import { useCallback, useState as classicUseState, useState } from 'react';

import { useMutation, useQuery } from 'react-query';

import { DateInput, SearchBarExpansive, Status } from '@components';
import { State } from '@hookstate/core';
import { IFilter, IFilterStatusData } from '@pages/Products/Products';
import { getManufacturersNames } from '@services/queries/manufacturers';

import { ReactComponent as ArrowRightImg } from '@assets/svg/arrow-right.svg';
import { ReactComponent as ArrowUpImg } from '@assets/svg/arrow-up.svg';
import {
  Checkbox,
  Row,
  Title,
} from '@components/Filters/FilterUpdates/components/MenuContent/MenuContent.styles';
import { indexEquipmentStatus } from '@services/queries/equipmentStatus';
import {
  clearFilter,
  updateFilterManufactorNames,
  updateFilterStatus,
  updateFilterTagsName,
  updateFilterUsers,
} from '@store/redux/features/filter';
import { useAppDispatch } from '@store/redux/store';

import AvatarDefaultImg from '@assets/svg/avatar-placeholder.svg';
import { ReactComponent as CheckboxChecked } from '@assets/svg/checkbox-checked.svg';
import {
  SearchBarExpansiveTags,
  SearchBarExpansiveUser,
} from '@components/SearchBars';
import {
  Avatar,
  Icon,
  Item,
  Label,
} from '@components/UpdatesPageMenus/Components/ReAssignDialog/ReAssignDialog.styles';
import { useSetState } from '@mantine/hooks';
import { IEquipmentStatus } from '@models/EquipmentStatus.model';
import {
  getUserMembers,
  IUserMember,
} from '@services/queries/studios/getUserMembers/getUserMembers';
import {
  DateFilters,
  updateFilterDates,
} from '@store/redux/features/filter/filterSlice';
import { BActionIcon } from '@components/BActionIcon/BActionIcon';
import { getTagsCategory } from '@services/queries/tagsCategory/getTagsCategory/getTagsCategory';
import { noop } from '@utils/noop';
import {
  Buttons,
  Cancel,
  ConfirmButton,
  Container,
  FilterDiv,
  Header,
} from './FilterContent.styles';
import { disableConfirmDate } from './FilterContent.utils';
import { TagsFilter } from './TagsFilter';
import { TagsFilterProps } from './TagsFilter/types';
import { OnTagFilterChange } from './OnTagFilterChange';

function Div(
  props: React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>,
) {
  const { children, ...rest } = props;
  return <div {...rest}>{children}</div>;
}
interface IAddFieldContent {
  openAddField: State<boolean>;
  filterData: IFilter;
  startSearch: () => void;
  filterCount: number;
}

const FilterContent = ({
  openAddField,
  filterData,
  startSearch,
  filterCount,
}: IAddFieldContent) => {
  const dispatch = useAppDispatch();
  const statusTransformer = useCallback(
    (data: IEquipmentStatus[]): IFilterStatusData[] => {
      const asFilter = data.map<IFilter['status'][number]>(status => {
        const isActive = filterData.status.some(
          filter => filter.status.id === status.id && filter.active,
        );
        return {
          active: isActive,
          status,
        };
      });

      return asFilter;
    },
    [filterData.status],
  );
  const { data: equipmentStatus = [] } = useQuery(
    'equipmentStatus',
    indexEquipmentStatus,
    {
      select: statusTransformer,
    },
  );
  const [tagSearch, setTagSearch] = useSetState({
    tagSearchText: '',
    tagQuerySearch: '',
  });

  const { data: usersData } = useQuery('members', getUserMembers);

  const [openManufacturer, setOpenManufacturer] = useState<boolean>(
    filterData.manufactorNames.length > 0,
  );
  const haveDateFilter = filterData.startDate || filterData.endDate;
  const [openPurchaseDate, setOpenPurchaseDate] = useState<boolean>(
    !(haveDateFilter === undefined || haveDateFilter === null),
  );
  const [openTag, setOpenTag] = useState<boolean>(filterData.tags.length > 0);
  const [openAssignee, setOpenAssignee] = useState<boolean>(
    filterData.users.length > 0,
  );
  const [openStatus, setOpenStatus] = useState<boolean>(
    filterData.status.length > 0,
  );

  const [userOptions, setUserOptions] = classicUseState<IUserMember[]>([]);
  const [searchTextStatus] = classicUseState('');
  classicUseState(false);
  const [searchTextUser, setSearchTextUser] = classicUseState('');
  classicUseState(false);
  const [searchTextManufactor, setSearchTextManufactor] = classicUseState('');
  const [loadingSearchManufactor, setLoadingSearchManufactor] =
    classicUseState(false);
  const [alreadySearchManufactor, setAlreadySearchManufactor] =
    classicUseState(false);
  const [showErasedSearchManufactor, setShowErasedManufactor] =
    classicUseState(false);
  const [loadingSearchTag, setLoadingSearchTag] = classicUseState(false);
  const [alreadySearchTag, setAlreadySearchTag] = classicUseState(false);
  const [showErasedSearchTag, setShowErasedTag] = classicUseState(false);
  const [manufactorNames, setManufactorNames] = classicUseState<string[]>([]);
  const [localDateFilter, setLocalDateFilter] = useSetState<DateFilters>({
    startDate: filterData.startDate ?? null,
    endDate: filterData.endDate ?? null,
  });
  const mutationGetManufactorsNames = useMutation(getManufacturersNames, {
    onSuccess: result => {
      setLoadingSearchManufactor(false);
      setShowErasedManufactor(true);
      setAlreadySearchManufactor(true);
      setManufactorNames(result.map(item => item.name));
    },
  });

  const tagsQuery = useQuery(
    ['tagCategories', tagSearch.tagQuerySearch],
    () =>
      getTagsCategory({
        search: tagSearch.tagQuerySearch,
      }),
    {
      onSuccess: () => {
        setLoadingSearchTag(false);
        setShowErasedTag(true);
        setAlreadySearchTag(true);
      },
    },
  );
  const { data = [], isLoading } = tagsQuery;

  const startSearchManufactor = (input: string) => {
    setLoadingSearchManufactor(true);
    mutationGetManufactorsNames.mutate(input);
  };

  const startSearchTag = () => {
    setLoadingSearchTag(true);
    setTagSearch(prev => {
      return { tagQuerySearch: prev.tagSearchText };
    });
  };

  const onPressEnterManufactor = (event: any) => {
    if (event.key === 'Enter') {
      startSearchManufactor(searchTextManufactor);
    }
  };

  const onPressEnterTag = (event: any) => {
    if (event.key === 'Enter') {
      startSearchTag();
    }
  };

  const searchFuncManufactor = (input: string) => {
    setShowErasedManufactor(false);
    setSearchTextManufactor(input);
  };

  const searchFuncTag = (input: string) => {
    setShowErasedTag(false);
    setTagSearch({ tagSearchText: input });
  };

  const searchFuncUsers = async (input: string) => {
    if (input.length > 0) {
      const newOptions = usersData
        ? usersData.filter(tag =>
            tag.name.toLowerCase().includes(input.toLowerCase()),
          )
        : [];
      setSearchTextUser(input);
      setUserOptions(newOptions);
    } else if (equipmentStatus) {
      setSearchTextUser(input);
      setUserOptions([]);
    }
  };
  const onTagFilterChange: TagsFilterProps['onTagFilterChange'] = useCallback(
    OnTagFilterChange(filterData.tags, filtered => {
      dispatch(updateFilterTagsName(filtered));
      startSearch();
    }),
    [dispatch, filterData.tags],
  );

  return (
    <Container
      aria-orientation="vertical"
      maxHeight="var(--max-height);"
      offsetScrollbars
    >
      <Header>
        <h2>Filter</h2>
        <BActionIcon
          onClick={() => {
            openAddField.set(false);
          }}
          icon="cross"
        />
      </Header>
      <Row onClick={() => setOpenManufacturer(!openManufacturer)}>
        <Title className="label">Manufacturer</Title>
        {openManufacturer ? <ArrowUpImg /> : <ArrowRightImg />}
      </Row>
      {openManufacturer && (
        <div className="FilterContainer">
          <SearchBarExpansive
            showOptions={manufactorNames}
            setShowOptions={setManufactorNames}
            searchText={searchTextManufactor}
            placeholder="Find a Manufacturer"
            loadingSearch={loadingSearchManufactor}
            alreadySearch={alreadySearchManufactor}
            showErasedSearch={showErasedSearchManufactor}
            searchOnKeyDown={onPressEnterManufactor}
            searchIconOnClick={() =>
              startSearchManufactor(searchTextManufactor)
            }
            closeIconOnClick={() => {
              searchFuncManufactor('');
            }}
            searchOnChange={searchFuncManufactor}
            onClickFilter={input => {
              const newFilterData = [...filterData.manufactorNames];
              const filterExist = newFilterData.find(item =>
                item.name.match(input),
              );
              if (!filterExist) {
                newFilterData.push({ name: input, active: true });
                dispatch(updateFilterManufactorNames(newFilterData));
              }
              setManufactorNames([]);
              startSearch();
            }}
          />

          {filterData.manufactorNames.map((manufacturerName, i) => (
            <Div
              className="OptionsDiv"
              onClick={() => {
                const newFilterData = [...filterData.manufactorNames];
                const index = newFilterData.indexOf(manufacturerName);
                if (index !== -1) {
                  const oldManufacturerName = newFilterData[index];
                  newFilterData[index] = {
                    name: oldManufacturerName.name,
                    active: !oldManufacturerName.active,
                  };
                  dispatch(updateFilterManufactorNames(newFilterData));
                  startSearch();
                }
              }}
              data-position={i}
            >
              <Checkbox checked={manufacturerName.active}>
                {manufacturerName.active ? <CheckboxChecked /> : ''}
              </Checkbox>
              <Title className="options">{manufacturerName.name}</Title>
            </Div>
          ))}
        </div>
      )}
      <Row onClick={() => setOpenPurchaseDate(!openPurchaseDate)}>
        <Title className="label">Purchase Date</Title>
        {openPurchaseDate ? <ArrowUpImg /> : <ArrowRightImg />}
      </Row>
      {openPurchaseDate && (
        <div className="FilterContainer">
          <Title className="date-labels">From</Title>
          <DateInput
            width="100%"
            height={42}
            placeholder="Pick Date"
            borderColor="#d8dae5"
            handleDateChange={e => {
              let startDate: Date | null = null;
              if (e) startDate = new Date(e);

              setLocalDateFilter({
                startDate,
              });
            }}
            value={localDateFilter.startDate}
            maxDate={localDateFilter.endDate ?? undefined}
          />

          <Title className="date-labels">To</Title>
          <DateInput
            width="100%"
            height={42}
            placeholder="Pick Date"
            borderColor="#d8dae5"
            handleDateChange={e => {
              let endDate: Date | null = null;
              if (e) endDate = new Date(e);
              setLocalDateFilter({
                endDate,
              });
            }}
            value={localDateFilter.endDate}
            minDate={localDateFilter.startDate ?? undefined}
          />
          <Buttons>
            <ConfirmButton
              disabled={disableConfirmDate(localDateFilter, filterData)}
              onClick={() => {
                dispatch(updateFilterDates(localDateFilter));
                startSearch();
              }}
            >
              Confirm
            </ConfirmButton>
          </Buttons>
        </div>
      )}
      <Row onClick={() => setOpenTag(!openTag)}>
        <Title className="label">Tag</Title>
        {openTag ? <ArrowUpImg /> : <ArrowRightImg />}
      </Row>
      {openTag && (
        <div className="FilterContainer">
          <SearchBarExpansiveTags
            showOptions={[]}
            setShowOptions={noop}
            searchText={tagSearch.tagSearchText}
            placeholder="Find a Tag"
            loadingSearch={loadingSearchTag || isLoading}
            alreadySearch={alreadySearchTag}
            showErasedSearch={showErasedSearchTag}
            searchOnKeyDown={onPressEnterTag}
            searchIconOnClick={() => startSearchTag()}
            closeIconOnClick={() => {
              setTagSearch({ tagSearchText: '', tagQuerySearch: '' });
            }}
            searchOnChange={searchFuncTag}
            onClickFilter={noop}
          />
          <TagsFilter
            searchText={tagSearch.tagQuerySearch}
            categories={data}
            selectedTags={filterData.tags}
            onTagFilterChange={onTagFilterChange}
          />
        </div>
      )}
      <Row onClick={() => setOpenStatus(!openStatus)}>
        <Title className="label">Status</Title>
        {openStatus ? <ArrowUpImg /> : <ArrowRightImg />}
      </Row>
      {openStatus && (
        <div className="FilterContainer">
          {equipmentStatus.map((status, i) => (
            <FilterDiv data-position={i}>
              <Div
                className="OptionsDiv"
                onClick={() => {
                  // flip
                  const newStatus = { ...status, active: !status.active };
                  // set.
                  const filterExceptCurrent = filterData.status.filter(
                    filter => filter.status.id !== status.status.id,
                  );
                  const filterWithCurrent =
                    filterExceptCurrent.concat(newStatus);
                  dispatch(updateFilterStatus(filterWithCurrent));
                  startSearch();
                }}
              >
                <Checkbox checked={status.active}>
                  {status.active ? <CheckboxChecked /> : ''}
                </Checkbox>
                <Status status={status.status} />
              </Div>
            </FilterDiv>
          ))}
        </div>
      )}
      <Row onClick={() => setOpenAssignee(!openAssignee)}>
        <Title className="label">Assignee</Title>
        {openAssignee ? <ArrowUpImg /> : <ArrowRightImg />}
      </Row>
      {openAssignee && (
        <div className="FilterContainer">
          <SearchBarExpansiveUser
            searchOnKeyDown={() => {}}
            showOptions={userOptions}
            setShowOptions={setUserOptions}
            searchText={searchTextUser}
            placeholder="Find a User"
            searchOnChange={searchFuncUsers}
            handleClickSearch={() => {
              if (searchTextStatus.length === 0 && usersData) {
                setUserOptions(userOptions.length > 0 ? [] : usersData);
              }
            }}
            closeIconOnClick={() => {
              setSearchTextUser('');
              setUserOptions([]);
            }}
            onClickFilter={input => {
              const newFilterData = [...filterData.users];
              const filterExist = newFilterData.find(item =>
                item.user.id.match(input.id),
              );
              if (!filterExist) {
                newFilterData.push({
                  user: input,
                  active: true,
                });
                dispatch(updateFilterUsers(newFilterData));
              }
              setUserOptions([]);
              startSearch();
            }}
          />
          {filterData.users.map((user, i) => (
            <FilterDiv data-position={i}>
              <Div
                className="OptionsDiv"
                onClick={() => {
                  const newFilterData = [...filterData.users];
                  const index = newFilterData.indexOf(user);
                  if (index !== -1) {
                    const oldUser = newFilterData[index];
                    newFilterData[index] = {
                      user: oldUser.user,
                      active: !oldUser.active,
                    };
                    dispatch(updateFilterUsers(newFilterData));
                    startSearch();
                  }
                }}
              >
                <Checkbox checked={user.active}>
                  {user.active ? <CheckboxChecked /> : ''}
                </Checkbox>
                <Item>
                  <Icon>
                    <Avatar
                      src={user.user?.picture || AvatarDefaultImg}
                      alt="Department"
                      width="24px"
                      height="24px"
                    />
                  </Icon>
                  <Label>{user.user?.name}</Label>
                </Item>
              </Div>
            </FilterDiv>
          ))}
        </div>
      )}
      <Buttons>
        <Cancel
          disabled={filterCount === 0}
          onClick={() => {
            dispatch(clearFilter());
            startSearch();
            openAddField.set(false);
          }}
        >
          Clear All
        </Cancel>
      </Buttons>
    </Container>
  );
};

export { FilterContent };
